import type { Theme } from "@mui/material/styles";
import {
  SIDENAV_MINI_WIDTH_PX,
  SIDENAV_WIDTH_PX,
} from "../tokens/sidenav";
import type {
  AppSidenavStyles,
  AppSx,
  MdTheme,
  SidenavCollapseOwnerState,
  SidenavDrawerOwnerState,
  SidenavFooterOwnerState,
} from "../types";

/** Icon/logo sizes stay fixed across expand and collapse. */
const NAV_ICON_PX = 24; // 32 × 0.75
const NAV_BRAND_PX = 44;

/** Soft space-grey for dark sidenav — avoids glare from near-white text. */
const DARK_NAV_INK = "#A4A9B4";
const DARK_NAV_MUTED = "#6F7582";
const DARK_NAV_EMPHASIS = "#C8CCD4";

function navInk(theme: MdTheme, emphasis = false) {
  if (theme.palette.mode !== "dark") {
    return theme.palette.text.primary;
  }
  return emphasis ? DARK_NAV_EMPHASIS : DARK_NAV_INK;
}

function buildCollapseItem(theme: MdTheme, ownerState: SidenavCollapseOwnerState = {}) {
  const { palette, transitions, breakpoints, borders, functions } = theme;
  const { active, miniSidenav } = ownerState;
  const { action } = palette;
  const { borderRadius } = borders;
  const { pxToRem } = functions;
  const ink = navInk(theme);

  return {
    position: "relative",
    backgroundColor: active ? action.selected : "transparent",
    color: `${ink} !important`,
    display: "flex",
    alignItems: "center",
    justifyContent: miniSidenav ? "center" : "flex-start",
    width: "100%",
    minHeight: pxToRem(40),
    padding: miniSidenav ? `${pxToRem(6)} ${pxToRem(4)}` : `${pxToRem(6)} ${pxToRem(10)}`,
    margin: miniSidenav ? `${pxToRem(1)} ${pxToRem(4)}` : `${pxToRem(1)} ${pxToRem(8)}`,
    borderRadius: borderRadius.sm,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    // Fluent selection indicator: short accent bar on the leading edge.
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      width: pxToRem(3),
      height: active ? pxToRem(16) : 0,
      borderRadius: pxToRem(2),
      backgroundColor: palette.primary.main,
      transform: "translateY(-50%)",
      transition: transitions.create("height", {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shortest,
      }),
    },
    [breakpoints.up("xl")]: {
      transition: transitions.create(["background-color"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shortest,
      }),
    },
    "&:hover": {
      backgroundColor: active ? action.selected : action.hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${palette.primary.main}`,
      outlineOffset: "-2px",
    },
  };
}

function buildCollapseIconBox(theme: MdTheme, ownerState: SidenavCollapseOwnerState = {}) {
  const { palette, borders, functions } = theme;
  const { active, miniSidenav } = ownerState;
  const { borderRadius } = borders;
  const { pxToRem } = functions;

  const iconColor = active
    ? `${palette.primary.main} !important`
    : `${navInk(theme)} !important`;
  const iconSize = pxToRem(NAV_ICON_PX);

  return {
    minWidth: iconSize,
    width: iconSize,
    minHeight: iconSize,
    height: iconSize,
    color: iconColor,
    borderRadius: borderRadius.md,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    marginRight: miniSidenav ? 0 : undefined,
    justifyContent: "center",
    "& .MuiIcon-root, & .material-icons": {
      fontSize: `${iconSize} !important`,
      width: iconSize,
      height: iconSize,
      lineHeight: iconSize,
    },
    "& svg": {
      color: `${iconColor} !important`,
      width: iconSize,
      height: iconSize,
      fontSize: iconSize,
    },
    "& svg g": {
      color: `${iconColor} !important`,
    },
  };
}

function buildCollapseIcon(theme: MdTheme, ownerState: SidenavCollapseOwnerState = {}) {
  const { palette, functions } = theme;
  const { active } = ownerState;
  const { pxToRem } = functions;
  const iconSize = pxToRem(NAV_ICON_PX);

  return {
    color: active ? `${palette.primary.main} !important` : `${navInk(theme)} !important`,
    fontSize: `${iconSize} !important`,
    width: iconSize,
    height: iconSize,
    lineHeight: iconSize,
  };
}

function buildCollapseText(
  theme: MdTheme,
  ownerState: SidenavCollapseOwnerState & { miniSidenav?: boolean } = {}
) {
  const { transitions, breakpoints, functions } = theme;
  const { miniSidenav, active } = ownerState;
  const { pxToRem } = functions;
  // 14px (md) + 1pt ≈ 15px; always bold per product request.
  const menuFontSize = pxToRem(15);
  const ink = navInk(theme, active);

  return {
    flex: miniSidenav ? "0 0 auto" : "1 1 0%",
    minWidth: 0,
    marginLeft: pxToRem(10),
    [breakpoints.up("lg")]: {
      opacity: miniSidenav ? 0 : 1,
      visibility: miniSidenav ? ("hidden" as const) : ("visible" as const),
      width: miniSidenav ? 0 : "auto",
      maxWidth: miniSidenav ? 0 : "none",
      flex: miniSidenav ? "0 0 auto" : "1 1 0%",
      marginLeft: miniSidenav ? 0 : pxToRem(10),
      overflow: miniSidenav ? "hidden" : "visible",
      transition: transitions.create(["opacity", "margin", "max-width", "flex"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },
    "& .MuiListItemText-primary, & .MuiTypography-root": {
      fontWeight: 400,
      fontSize: menuFontSize,
      lineHeight: 1.43,
      color: `${ink} !important`,
    },
  };
}

function buildDrawerRoot(theme: MdTheme, ownerState: SidenavDrawerOwnerState = {}) {
  const { palette, transitions, breakpoints, functions } = theme;
  const { transparentSidenav = false, whiteSidenav = false, miniSidenav = false, darkMode = false } =
    ownerState;

  const sidebarWidth = SIDENAV_WIDTH_PX;
  const { transparent, background, divider } = palette;
  const { pxToRem } = functions;
  // Mobile overlay needs elevation; the docked pane relies on a hairline instead.
  const drawerShadow = "none";
  const overlayShadow = theme.boxShadows.xl;

  let backgroundValue: string = background.sidenav;

  if (transparentSidenav) {
    backgroundValue = transparent.main;
  } else if (whiteSidenav) {
    backgroundValue = background.paper;
  }

  const drawerOpenStyles = () => ({
    position: "fixed !important",
    top: "0 !important",
    left: "0 !important",
    height: "100vh !important",
    width: `${sidebarWidth}px`,
    background: backgroundValue,
    transform: "translateX(0)",
    zIndex: 1200,
    overflowY: "auto",
    overflowX: "hidden",
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),
    [breakpoints.up("xl")]: {
      boxShadow: drawerShadow,
      marginBottom: transparentSidenav ? 0 : "inherit",
      left: "0",
      width: sidebarWidth,
      transform: "translateX(0)",
      transition: transitions.create(["width", "background-color"], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.enteringScreen,
      }),
    },
  });

  const drawerCloseStyles = () => ({
    position: "fixed !important",
    top: "0 !important",
    left: "0 !important",
    height: "100vh !important",
    background: backgroundValue,
    transform: `translateX(${pxToRem(-320)})`,
    zIndex: 1200,
    overflowY: "auto",
    overflowX: "hidden",
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),
    [breakpoints.down("lg")]: {
      transform: `translateX(${pxToRem(-320)}) !important`,
      visibility: "hidden",
      opacity: 0,
    },
    [breakpoints.up("lg")]: {
      boxShadow: drawerShadow,
      marginBottom: transparentSidenav ? 0 : "inherit",
      left: "0",
      width: `${pxToRem(SIDENAV_MINI_WIDTH_PX)} !important`,
      minWidth: `${pxToRem(SIDENAV_MINI_WIDTH_PX)} !important`,
      maxWidth: `${pxToRem(SIDENAV_MINI_WIDTH_PX)} !important`,
      boxSizing: "border-box",
      overflowX: "hidden",
      transform: "translateX(0)",
      visibility: "visible",
      opacity: 1,
      transition: transitions.create(["width", "min-width", "max-width", "background-color"], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.shorter,
      }),
    },
  });

  return {
    position: "fixed",
    zIndex: 1200,
    [breakpoints.down("lg")]: {
      display: miniSidenav ? "none" : "block",
    },
    "& .MuiDrawer-paper": {
      boxShadow: drawerShadow,
      border: "none",
      borderRight: `1px solid ${divider}`,
      borderRadius: 0,
      [breakpoints.down("lg")]: { boxShadow: overlayShadow },
      ...(miniSidenav ? drawerCloseStyles() : drawerOpenStyles()),
    },
  } as AppSx;
}

function buildLogoLabel(theme: MdTheme, miniSidenav = false) {
  const { transitions, breakpoints } = theme;

  return {
    display: miniSidenav ? "none" : "flex",
    alignItems: "baseline",
    gap: "0.28em",
    ml: 1,
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    transition: transitions.create(["opacity"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.shorter,
    }),
    [breakpoints.up("lg")]: {
      display: miniSidenav ? "none" : "flex",
    },
  };
}

export function buildSidenavStyles(theme: Theme): AppSidenavStyles {
  const mdTheme = theme as MdTheme;
  const { transitions, breakpoints, functions } = mdTheme;
  const { pxToRem } = functions;

  return {
    drawerRoot: (ownerState) => buildDrawerRoot(mdTheme, ownerState),
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: "100vh",
    },
    header: (miniSidenav = false) => ({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      pt: 2,
      pb: 1,
      // Phone drawer: leave room on both sides so the close button never covers the logo.
      px: { xs: 7, lg: 2 },
      minHeight: { xs: 88, lg: 88 },
      ...(miniSidenav
        ? {
            pt: { lg: 1.5 },
            pb: { lg: 1 },
            px: { lg: 0.75 },
          }
        : {}),
    }),
    brandRow: (miniSidenav = false) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: miniSidenav ? "center" : "flex-start",
      gap: miniSidenav ? 0 : 0.5,
      width: "100%",
      textDecoration: "none",
      color: "inherit",
      minWidth: 0,
    }),
    brandImage: () => ({
      width: pxToRem(NAV_BRAND_PX),
      maxWidth: pxToRem(NAV_BRAND_PX),
      height: "auto",
      maxHeight: pxToRem(NAV_BRAND_PX),
      objectFit: "contain",
      display: "block",
      flexShrink: 0,
    }),
    logoLabel: (miniSidenav = false) => buildLogoLabel(mdTheme, miniSidenav),
    logoConnect: {
      fontFamily: `"Segoe UI", "Helvetica Neue", Arial, sans-serif`,
      fontSize: pxToRem(22),
      fontWeight: 500,
      letterSpacing: "-0.03em",
      color: mdTheme.palette.mode === "dark" ? DARK_NAV_MUTED : "text.secondary",
      lineHeight: 1,
    },
    logoAi: {
      fontFamily: `"Segoe UI", "Helvetica Neue", Arial, sans-serif`,
      fontSize: pxToRem(22),
      fontWeight: 800,
      letterSpacing: "-0.045em",
      color: mdTheme.palette.mode === "dark" ? DARK_NAV_EMPHASIS : "text.primary",
      lineHeight: 1,
    },
    list: {
      flex: 1,
      overflow: "auto",
      py: 0,
    },
    footer: (ownerState: SidenavFooterOwnerState = {}) => {
      void ownerState;
      return {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        py: 1.25,
        px: 0.5,
        borderTop: 1,
        borderColor: "divider",
      };
    },
    footerToggleButton: () => ({
      color: "text.secondary",
    }),
    footerPinButton: (ownerState = {}) => {
      const { sidenavPinned = false } = ownerState;
      return {
        color: sidenavPinned ? "primary.main" : "text.secondary",
      };
    },
    pinIcon: {
      fontSize: 20,
    },
    mobileBackdrop: {
      zIndex: 1199,
      cursor: "pointer",
    },
    mobileCloseWrap: {
      display: { xs: "block", lg: "none" },
      position: "absolute",
      top: 0,
      right: 0,
      p: 1.625,
      cursor: "pointer",
    },
    closeIcon: {
      fontWeight: "bold",
    },
    navLink: {
      textDecoration: "none",
    },
    collapseItem: (ownerState) => buildCollapseItem(mdTheme, ownerState),
    collapseIconBox: (ownerState) => buildCollapseIconBox(mdTheme, ownerState),
    collapseIcon: (ownerState) => buildCollapseIcon(mdTheme, ownerState),
    collapseText: (ownerState) => buildCollapseText(mdTheme, ownerState),
    collapsibleItem: (ownerState) => ({
      ...buildCollapseItem(mdTheme, ownerState),
      cursor: "pointer",
    }),
    expandIcon: (open = false) => ({
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s cubic-bezier(0.33, 0, 0.67, 1)",
      fontSize: "1.1rem",
      flexShrink: 0,
    }),
    subItem: {
      pl: 2.5,
    },
  };
}
